// Path: src/components/user/shoppage/bookingmodal/EquipmentBookingModal.jsx

import React, { useState, useMemo, useEffect } from "react";
import { FiX, FiMapPin, FiCheck, FiLock } from "react-icons/fi";
import "../../../../assets/css/user/shoppage/bookingform/BookingForm.css";
import axios from "axios";
import { useContext } from "react";
import AppContext from "../../../../context/AppContext.js";
import Swal from "sweetalert2";

const SHIPPING_FEE = 250; // flat home delivery charge — adjust/remove if backend sends this

const EquipmentBookingModal = ({ show, onHide, equipmentData }) => {
  const equipment = equipmentData?.equipment;
  const owner = equipment?.shopName || "Unknown Shop";
  const { baseUrl, loggedInCustomer, loggedInRental } = useContext(AppContext);
  const [payment, setPayment] = useState("cod");
  const [shippingMethod, setShippingMethod] = useState("home_delivery");

  const [loading, setLoading] = useState(false);

  // 👇 states same rakhi hain
  const [bookingdate, setBookingDate] = useState("");
  const [duration, setDuration] = useState("");
  const [location, setLocation] = useState("");

  // ── NEW: rental basis (hour / day / acre) ──
  const [rentalBasis, setRentalBasis] = useState("");

  const [errors, setErrors] = useState({});

  // ── Available rate options based on jo admin ne fill ki hain ──
  const rateOptions = useMemo(() => {
    if (!equipment) return [];
    const options = [];
    if (equipment.rate_per_hour) {
      options.push({ basis: "hour", label: "Per Hour", unitLabel: "Hours", rate: Number(equipment.rate_per_hour) });
    }
    if (equipment.rate_per_day) {
      options.push({ basis: "day", label: "Per Day", unitLabel: "Days", rate: Number(equipment.rate_per_day) });
    }
    if (equipment.rate_per_acre) {
      options.push({ basis: "acre", label: "Per Acre", unitLabel: "Acres", rate: Number(equipment.rate_per_acre) });
    }
    return options;
  }, [equipment]);

  const selectedRateOption = rateOptions.find((o) => o.basis === rentalBasis);

  const requestForBooking = () => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Login Required",
        text: "Please login first",
      });

      return;
    }

    const customer = JSON.parse(localStorage.getItem("LoggedInCustomer"));
    const durationFloat = parseFloat(duration);

    const formData = new FormData();
    formData.append("srcustomer_id", loggedInCustomer.id);
    formData.append("srrentaladmin_id", equipment?.srrentaladmin_id);
    formData.append("bookingdate", bookingdate);
    formData.append("duration", durationFloat);
    formData.append("rentalbasis", rentalBasis);
    formData.append("location", location);
    formData.append("srequipmentmanagment_id", equipment?.id);

    formData.append("equipmentname", equipment?.name);
    formData.append("shippingmethod", shippingMethod);
    formData.append("shippingfee", SHIPPING_FEE);
    formData.append("paymentmethod", payment);
    formData.append("totalprice", totalPrice);

    for (let pair of formData.entries()) {
      console.log(pair[0], pair[1]);
    }
    // ── Booking ab HAMESHA pehle create hoti hai (COD ho ya Stripe) ──
    // Payment status "pending" set hoti hai backend par khud (frontend se
    // "already paid" bhejna possible nahi). Stripe ke liye, booking create
    // hone ke baad usi booking_id se checkout session banti hai — amount
    // bhi backend booking se uthata hai, request se nahi.
    axios
      .post(`${baseUrl}/customer/booking/requestForBooking`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        const createdBooking = response.data.booking;

        if (!response.data.success || !createdBooking) {
          setLoading(false);
          Swal.fire({
            icon: "error",
            title: "Oops!",
            text: response.data.message || "Failed to create booking.",
          });
          return;
        }

        // COD — booking created, done.
        if (payment !== "stripe") {
          setLoading(false);

          Swal.fire({
            title: "Congratulations!",
            text: "Booking Created Successfully",
            icon: "success",
          });

          setBookingDate("");
          setDuration("");
          setLocation("");
          setErrors({});
          onHide();
          return;
        }

        // Stripe — start checkout session for the booking we just created.
        axios
          .post(
            `${baseUrl}/create-checkout-session`,
            { booking_id: createdBooking.id },
            { headers: { Authorization: `Bearer ${token}` } },
          )
          .then((res) => {
            setLoading(false);
            const stripeUrl = res.data.url;

            if (res.data.success && stripeUrl) {
              window.location.href = stripeUrl;
              return;
            }

            Swal.fire({
              icon: "error",
              title: "Unable to start payment",
              text: res.data.message || "Please try again.",
            });
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
            Swal.fire({
              title: "Error!",
              text: "Something went wrong while starting payment",
              icon: "error",
            });
          });
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          title: "Error!",
          text: "Something went wrong",
          icon: "error",
        });
      });
  };

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setBookingDate("");
      setDuration("");
      setLocation("");
      setPayment("cod");
      setShippingMethod("home_delivery");
      setErrors({});

      // ── Default rentalBasis: pehla available option select ho jaye ──
      if (rateOptions.length > 0) {
        setRentalBasis(rateOptions[0].basis);
      }

      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show, rateOptions]);

  // total price (quantity × selected rate + flat shipping fee)
  const totalPrice = useMemo(() => {
    if (!selectedRateOption) return 0;

    const qty = parseFloat(duration);

    if (!qty || qty <= 0) return 0;

    return qty * selectedRateOption.rate + SHIPPING_FEE;
  }, [duration, selectedRateOption]);

  // validation
  const validateForm = () => {
    const newErrors = {};
    if (!bookingdate) {
      newErrors.bookingdate = "Date is required";
    }

    if (!rentalBasis) {
      newErrors.rentalBasis = "Please select a rental basis";
    }

    if (!duration || parseFloat(duration) <= 0) {
      newErrors.duration = `Enter valid ${selectedRateOption?.unitLabel?.toLowerCase() || "quantity"}`;
    }

    if (!location.trim()) {
      newErrors.location = "Location is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // submit
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    requestForBooking();
  };

  if (!show || !equipment || !owner) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="bam-backdrop" onClick={onHide} />

      {/* Booking Modal */}
      <div className="bam-modal">
        <div className="bam-modal-content">
          {/* Header */}
          <div className="bam-header">
            <div className="bam-header-content">
              <div className="bam-icon-wrapper">
                <FiMapPin className="bam-header-icon" />
              </div>

              <div>
                <h2 className="bam-title">Book Equipment</h2>

                <p className="bam-subtitle">
                  {equipment.name} — {owner}
                </p>
              </div>
            </div>

            <button className="bam-close-btn" onClick={onHide}>
              <FiX size={24} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bam-form">
            <div className="bam-form-body">
              {/* Booking Date */}
              <div className="bam-form-group">
                <label className="bam-label">
                  Booking Date <span className="bam-required">*</span>
                </label>

                <input
                  type="date"
                  name="bookingDate"
                  min={new Date().toISOString().split("T")[0]}
                  value={bookingdate}
                  onChange={(e) => {
                    setBookingDate(e.target.value);
                  }}
                  className={`bam-input ${
                    errors.bookingdate ? "bam-input-error" : ""
                  }`}
                />

                {errors.bookingdate && (
                  <span className="bam-error-text">{errors.bookingdate}</span>
                )}
              </div>

              {/* Equipment */}
              <div className="bam-form-group">
                <label className="bam-label">Equipment</label>

                <input
                  type="text"
                  value={equipment.name}
                  disabled
                  className="bam-input"
                />
              </div>

              {/* Owner */}
              <div className="bam-form-group">
                <label className="bam-label">Owner / Shop</label>

                <input
                  type="text"
                  value={owner}
                  disabled
                  className="bam-input"
                />
              </div>

              {/* ── NEW: Rental Basis Selector ── */}
              <div className="bam-form-group">
                <label className="bam-label">
                  Rent By <span className="bam-required">*</span>
                </label>

                {rateOptions.length > 0 ? (
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    {rateOptions.map((opt) => (
                      <div
                        key={opt.basis}
                        onClick={() => {
                          setRentalBasis(opt.basis);
                          setDuration("");
                        }}
                        style={{
                          flex: "1 1 30%",
                          minWidth: "120px",
                          border: rentalBasis === opt.basis ? "2px solid #FDB714" : "1px solid #ddd",
                          background: rentalBasis === opt.basis ? "#FFF8E7" : "#fff",
                          borderRadius: "8px",
                          padding: "10px 12px",
                          cursor: "pointer",
                          textAlign: "center",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: "13px" }}>{opt.label}</div>
                        <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                          Rs. {opt.rate.toLocaleString("en-PK")}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: "13px", color: "#999" }}>No rates available for this equipment.</p>
                )}

                {errors.rentalBasis && (
                  <span className="bam-error-text">{errors.rentalBasis}</span>
                )}
              </div>

              {/* Duration / Quantity — dynamic label based on rentalBasis */}
              <div className="bam-form-group">
                <label className="bam-label">
                  {selectedRateOption ? `Duration (${selectedRateOption.unitLabel})` : "Quantity"}{" "}
                  <span className="bam-required">*</span>
                </label>

                <input
                  type="number"
                  name="duration"
                  min="1"
                  step={rentalBasis === "acre" ? "0.5" : "1"}
                  value={duration}
                  onChange={(e) => {
                    setDuration(e.target.value);
                  }}
                  className={`bam-input ${
                    errors.duration ? "bam-input-error" : ""
                  }`}
                  placeholder={
                    selectedRateOption
                      ? `Enter ${selectedRateOption.unitLabel.toLowerCase()}`
                      : "Select rental basis first"
                  }
                  disabled={!selectedRateOption}
                />

                {errors.duration && (
                  <span className="bam-error-text">{errors.duration}</span>
                )}
              </div>

              {/* Location */}
              <div className="bam-form-group">
                <label className="bam-label">
                  Full Address <span className="bam-required">*</span>
                </label>

                <input
                  type="text"
                  name="location"
                  value={location}
                  onChange={(e) => {
                    setLocation(e.target.value);
                  }}
                  className={`bam-input ${
                    errors.location ? "bam-input-error" : ""
                  }`}
                  placeholder="Enter field/full address"
                />

                {errors.location && (
                  <span className="bam-error-text">{errors.location}</span>
                )}
              </div>

              {/* Payment */}
              <div className="bam-section">
                <h3 className="bam-section-title">Payment</h3>

                <p className="bam-secure-note">
                  <FiLock size={14} />
                  All transactions are secure and encrypted.
                </p>

                <div
                  className={`bam-radio-option ${
                    payment === "stripe" ? "bam-radio-option-selected" : ""
                  }`}
                  onClick={() => setPayment("stripe")}
                >
                  <div className="bam-radio-option-left">
                    <span
                      className={`bam-radio-circle ${
                        payment === "stripe" ? "bam-radio-circle-filled" : ""
                      }`}
                    />
                    <span className="bam-radio-label">
                      STRIPE{" "}
                      <span className="bam-radio-sublabel">
                        (Debit / Credit / Wallet / Bank Account)
                      </span>
                    </span>
                  </div>

                  <div className="bam-card-badges">
                    <span className="bam-card-badge">VISA</span>
                    <span className="bam-card-badge">MC</span>
                  </div>
                </div>
              </div>

              {/* Total Price */}
              <div className="total-box">
                <span>Total Price</span>

                <strong>Rs. {totalPrice.toLocaleString("en-PK")}</strong>
              </div>
            </div>

            {/* Footer */}
            <div className="bam-footer">
              <button
                type="button"
                className="bam-btn bam-btn-cancel"
                onClick={onHide}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="bam-btn bam-btn-confirm"
                disabled={loading || rateOptions.length === 0}
              >
                <FiCheck size={18} />

                {loading ? "Processing..." : "Confirm Booking"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EquipmentBookingModal;